import { ICONS } from "@assets/icons";
import Button from "@components/atoms/Button";
import ContentBox from "@components/atoms/ContentBox";
import IconBox from "@components/atoms/IconBox";
import Editor from "@components/molecules/Editor";
import {
    useV2FAQsControllerFetchFaqDetail,
    useV2FAQsControllerUpdateFaq,
    useV2FAQsControllerDeleteFaq,
    type V2FaqUpdateRequestDto,
    type V2FaqUpdateRequestDtoMainCategory,
    type V2FaqUpdateRequestDtoMiddleCategory,
} from "@everon-it/api-client/contents-gw";
import { useCascadingCategory } from "@hooks/useCascadingCategory";
import { useFetchFaqCategoryOption } from "@hooks/useFetchFaqCategoryOption";
import useGlobalModal from "@hooks/useGlobalModal";
import useGlobalNoti from "@hooks/useGlobalNoti";
import useRouter from "@hooks/useRouter";
import { FaqNoti } from "@pages/FaqCreate";
import { type FaqCommonContentsOptionType } from "@pages/FaqCreate/Data";
import { colors, typo } from "@styles/index";
import { CommonElementType, RenderCommonElementByType } from "@utils/DynamicComponentUtils";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { compareObjects } from "@utils/data-fns";
import CopyButton from "@components/atoms/CopyButton";
import useAuthStore from "@store/auth";
import { FaqDetailData, type FaqDetailInitValuesType } from "./Data";

const FaqDetail: React.FC = () => {
    // Props
    const HEADER_TITLE = "FAQ 상세";
    const CONTENT_TITLE = "FAQ 내용";

    // Data
    const { formInitialValues } = FaqDetailData;

    // Hooks
    const { query, goBack } = useRouter();
    const { hideModal, showConfirmModal, showReasonModificationModal, showReasonEditModal } = useGlobalModal();
    const { showEditNoti, showDeleteNoti } = useGlobalNoti();
    const { userInfo } = useAuthStore();
    const uuid = String(query.id || "");
    const [initData, setInitData] = useState<FaqDetailInitValuesType>();

    // api-client: 상세 조회 (V2)
    const { data: detailData } = useV2FAQsControllerFetchFaqDetail(uuid, {
        query: {
            enabled: !!uuid,
            retry: false,
            refetchOnWindowFocus: false,
        },
    });

    // api-client: 수정 mutation (V2)
    const { mutate: editMutate } = useV2FAQsControllerUpdateFaq({
        mutation: {
            onSuccess: () => {
                showEditNoti({});
                goBack();
            },
        },
    });

    // api-client: 삭제 mutation (V2)
    const { mutate: deleteMutate } = useV2FAQsControllerDeleteFaq({
        mutation: {
            onSuccess: () => {
                showDeleteNoti({});
                goBack();
            },
        },
    });

    // Formik
    const formik = useFormik({
        initialValues: formInitialValues,
        onSubmit: values => {
            const body: V2FaqUpdateRequestDto = {
                title: values.title,
                content: values.content,
                isVisible: values.isVisible,
                mainCategory: values.mainCategory as V2FaqUpdateRequestDtoMainCategory,
                middleCategory: values.middleCategory as V2FaqUpdateRequestDtoMiddleCategory,
                reason: values.reason,
                updatedBy: userInfo?.name ?? "",
            };
            editMutate({ uuid, data: body });
        },
    });

    // 상세 데이터 formik에 반영
    useEffect(() => {
        if (detailData?.row) {
            const row = detailData.row;
            const customData: FaqDetailInitValuesType = {
                title: row.title,
                isVisible: row.isVisible,
                mainCategory: row.mainCategory,
                middleCategory: row.middleCategory ?? "",
                content: row.content ?? "",
                reason: "",
                updateHistory: row.updateHistory ?? [],
            };
            setInitData(customData);
            void formik.setValues(customData);
        }
    }, [detailData]);

    const { categoryOption: mainCategoryOption } = useFetchFaqCategoryOption();
    const { categoryOption: middleCategoryOption, setCategoryOption: setMiddleCategoryOption } = useFetchFaqCategoryOption(formik.values.mainCategory);

    // 상위 카테고리 변경 시 하위 카테고리 초기화
    useCascadingCategory({
        formik,
        mainCategoryField: "mainCategory",
        subCategoryField: "middleCategory",
        setSubCategoryOption: setMiddleCategoryOption,
        preserveInitialSubCategory: true,
    });

    // Functions
    const handleGoBackClick = () => {
        if (!initData || !compareObjects(initData, formik.values)) {
            showConfirmModal({
                title: `작성한 내용을 저장하지 않고\n 취소 하시겠어요?`,
                firstButtonText: "닫기",
                secondButtonText: "확인",
                onConfirm: () => {
                    hideModal();
                    goBack();
                },
            });
        } else {
            goBack();
        }
    };

    const handleEditContent = () => {
        showReasonEditModal({
            category: "faq",
            onSave: () => {
                localStorage.removeItem("previewData");
                hideModal();
                formik.handleSubmit();
            },
            onChangeReason: e => {
                void formik.setFieldValue("reason", e.target.value);
            },
        });
    };

    const handleDeleteContent = () => {
        showConfirmModal({
            title: `삭제하시겠어요?`,
            firstButtonText: "닫기",
            secondButtonText: "삭제하기",
            onConfirm: () => {
                deleteMutate({ uuid });
                hideModal();
            },
        });
    };

    const handleReasonModification = () => {
        showReasonModificationModal({
            category: "faq",
            updateHistory: formik.values.updateHistory,
        });
    };

    const isEditButtonValidate = useCallback(() => {
        if (!initData) {
            return true;
        }
        const { title, mainCategory, middleCategory, content } = formik.values;
        const isCompare = compareObjects(initData, formik.values);

        return isCompare || !title || !mainCategory || !middleCategory || !content;
    }, [formik.values, initData]);

    // Renderers Data
    const contentsOption: FaqCommonContentsOptionType[] = [
        {
            label: "노출 상태",
            name: "isVisible",
            type: CommonElementType.TOGGLE,
        },
        {
            label: "대분류",
            name: "mainCategory",
            type: CommonElementType.SELECT,
            placeholder: "대분류를 선택해주세요",
            options: mainCategoryOption,
        },
        {
            label: "중분류",
            name: "middleCategory",
            type: CommonElementType.SELECT,
            placeholder: "중분류를 선택해주세요",
            notice: FaqNoti,
            options: middleCategoryOption,
        },
        {
            label: "제목",
            name: "title",
            type: CommonElementType.INPUT,
            placeholder: "제목을 입력해주세요",
        },
    ];

    return (
        <ContentsWrap>
            <HeaderWrap>
                <IconBox width={32} height={32} src={ICONS.ARROW_ICON.BACK} isCursorPointer onClick={handleGoBackClick} />
                <HeaderTitle>{HEADER_TITLE}</HeaderTitle>
            </HeaderWrap>
            <div>
                <ContentBox style={{ padding: 0 }}>
                    <ContentsHeader>
                        {CONTENT_TITLE}
                        <ButtonWrap>
                            <CopyButton text="외부 링크 복사" type="external" />
                            <CopyButton text="앱 연결 링크 복사" type="app" />
                        </ButtonWrap>
                    </ContentsHeader>
                    <ContentsMainWrap>
                        {contentsOption.map(({ label, name, options, notice, ...res }, _) => {
                            return (
                                <React.Fragment key={`${_}_contents`}>
                                    <InputWrap>
                                        <LabelWrap>
                                            <LabelInnerWrap>
                                                <Label>{label || "LABEL"}</Label>
                                            </LabelInnerWrap>
                                        </LabelWrap>
                                        <InputContentsWrap noti={!!notice}>
                                            {RenderCommonElementByType({
                                                formik,
                                                name,
                                                options,
                                                ...res,
                                            })}
                                            {notice && notice}
                                        </InputContentsWrap>
                                    </InputWrap>
                                </React.Fragment>
                            );
                        })}
                        <Editor formik={formik} initData={formik.values.content} />
                    </ContentsMainWrap>
                </ContentBox>
                <FooterButtonWrap>
                    <LeftButtonWrap>
                        <Button text="삭제" buttonType="line_red" onClick={handleDeleteContent} />
                        <Button text="수정사유 확인" buttonType="line_primary" onClick={handleReasonModification} disabled={formik.values.updateHistory.length === 0} />
                    </LeftButtonWrap>
                    <RightButtonWrap>
                        <Button text="취소" buttonType="line_black" onClick={handleGoBackClick} />
                        <Button disabled={isEditButtonValidate()} text="저장하기" onClick={handleEditContent} />
                    </RightButtonWrap>
                </FooterButtonWrap>
            </div>
        </ContentsWrap>
    );
};

export default FaqDetail;

const ContentsWrap = styled.div`
    background-color: ${colors.GRAY11};
    height: 100%;
    padding: 40px;
    overflow: auto;
`;

const HeaderWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
`;

const MainHeaderTitle = styled.h1`
    ${typo.DISPLAY_3}
    color: ${colors.GRAY2};
    margin-bottom: 24px;
`;

const HeaderTitle = styled(MainHeaderTitle)`
    margin: 0;
`;

const TableTitle = styled.div`
    color: ${colors.GRAY2};
    ${typo.HEADING_4};
    margin-bottom: 18px;
`;

const ContentTitle = styled(TableTitle)`
    margin: 0;
    padding: 24px;
    border-bottom: 1px solid ${colors.GRAY9};
`;

const ButtonWrap = styled.div`
    display: flex;
    gap: 12px;
`;

const ContentsHeader = styled(ContentTitle)`
    display: flex;
    justify-content: space-between;
`;

const ContentsMainWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 840px;
    margin: auto;
    padding: 52px 0 80px;
`;

const InputWrap = styled.div`
    display: flex;
    gap: 12px;
    min-height: 48px;
    width: 100%;
`;

const LabelWrap = styled.span`
    min-width: 200px;
    padding: 12px 0 12px;
`;

const LabelInnerWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;
const Label = styled.p`
    color: ${colors.GRAY6};
    ${typo.HEADING_7};
`;

const InputContentsWrap = styled.div<{ noti?: boolean }>`
    width: 100%;
    display: flex;
    align-items: center;

    ${({ noti }) =>
        noti &&
        css`
            flex-direction: column;
            gap: 12px;
        `}
`;

const FooterButtonWrap = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 24px;
`;

const LeftButtonWrap = styled.div`
    display: flex;
    gap: 12px;
`;

const RightButtonWrap = styled.div`
    display: flex;
    gap: 12px;
`;
